import React, { useState, useEffect } from 'react';
import { Divider, Grid, Box, CircularProgress, Typography } from '@material-ui/core';
import StorageIcon from '@material-ui/icons/Storage';
import BarChartIcon from '@material-ui/icons/BarChart';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import BuildIcon from '@material-ui/icons/Build';

// Styles
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";

// Custom Components
import DataRequirements from "./DataRequirements";
import GeneralRequirements from "./GeneralRequirements";
import VisualizationRequirements from "./VisualizationRequirements";
import LabDesignTiles from 'components/Tiles/LabDesignTiles';
import AdditionalDetails from './AdditionalDetails';

import { fetchLabAPI, saveLabInstructionsAPI, saveStreamlitInstructionsAPI } from "views/APIs/APIUtility";

// Contexts
import NotificationContext from "views/Components/Context.js";

import { _mapResourceToAppType } from "../CommonFunctions";

const useStyles = makeStyles((theme) => ({
    ...styles,
}));


export default function LabDesignStep({ labID, setStage, setActiveStep, designType = "notebook" }) {
    const classes = useStyles();
    const { showNotification } = React.useContext(NotificationContext);
    const tileOrder = ["General Requirements", "Data Requirements", "Visualization Requirements", "Additional Details"];
    const [activeTile, setActiveTile] = useState(tileOrder[0]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState({ isLoading: false, message: "" });
    const [lab, setLab] = useState(null);
    const [instructions, setInstructions] = useState({});
    const [streamlitInstructions, setStreamlitInstructions] = useState({});
    const [newResource, setNewResource] = useState({ name: "", description: "", file: null });
    const [replace, setReplace] = useState(false);

    // Determine which instructions to use based on designType
    const currentInstructions = designType === "streamlit" ? streamlitInstructions : instructions;
    const setCurrentInstructions = designType === "streamlit" ? setStreamlitInstructions : setInstructions;

    useEffect(() => {
        async function fetchLabDetails() {
            try {
                setLoading({ isLoading: true, message: "Fetching Data Requirements..." });
                const data = await fetchLabAPI(labID);
                setLab(data);
                setInstructions(data.instructions);
                setStreamlitInstructions(data.streamlit_instructions || {});
                handleSetFileResources(data.raw_resources);
                setLoading({ isLoading: false, message: "" });
            } catch (error) {
                console.error("Error in fetchLabDetails:", error);
                showNotification("Error fetching lab details", "danger");
            }
        }
        fetchLabDetails();
    }, [labID]);

    const handleSetFileResources = (resources) => {
        const mappedResources = resources
            .filter(resource => resource.resource_type === "Dataset" || resource.resource_type === "Image")
            .map(_mapResourceToAppType);
        setFiles(mappedResources);
    };

    const handleSave = async (showLoader = true, customMessage = "Saving...") => {
        try {
            if (showLoader) {
                setLoading({ isLoading: true, message: customMessage });
            }
            const formData = new FormData();
            formData.append("lab_id", labID);
            formData.append("instructions", JSON.stringify(currentInstructions));

            // Use the appropriate API based on designType
            if (designType === "streamlit") {
                await saveStreamlitInstructionsAPI(formData);
            } else {
                await saveLabInstructionsAPI(formData);
            }
        } catch (error) {
            console.error("Error in handleSave:", error);
            showNotification("Error saving lab details", "danger");
        } finally {
            if (showLoader) {
                setLoading({ isLoading: false, message: "" });
            }
        }
    };

    const descriptions = {
        resources: "Add the learning outcomes for the lab.",
        datasets: "Add the data creation strategy for the lab. You can also add your own datasets.",
        visualization: "Add any visualization requirements for the lab. You can also add images/screenshots.",
        additional: "Add any additional details for the lab."
    };

    const handleNavigation = (tile) => {
        setActiveTile(tile);
    };

    const renderContent = () => {
        const contentMap = {
            "General Requirements": (
                <GeneralRequirements
                    key={`general-${designType}`}
                    setActiveStep={setActiveStep}
                    description={descriptions.resources}
                    handleNavigation={handleNavigation}
                    labID={labID}
                    instructions={currentInstructions}
                    setInstructions={setCurrentInstructions}
                    handleSave={handleSave}
                />
            ),
            "Data Requirements": (
                <DataRequirements
                    key={`data-${designType}`}
                    description={descriptions.datasets}
                    handleNavigation={handleNavigation}
                    handleSetFileResources={handleSetFileResources}
                    labID={labID}
                    newResource={newResource}
                    setNewResource={setNewResource}
                    replace={replace}
                    setReplace={setReplace}
                    files={files.filter(file => file.type === "Dataset")}
                    setFiles={setFiles}
                    instructions={currentInstructions}
                    setInstructions={setCurrentInstructions}
                    handleSave={handleSave}
                />
            ),
            "Visualization Requirements": (
                <VisualizationRequirements
                    key={`visualization-${designType}`}
                    description={descriptions.visualization}
                    handleNavigation={handleNavigation}
                    handleSetFileResources={handleSetFileResources}
                    labID={labID}
                    newResource={newResource}
                    setNewResource={setNewResource}
                    replace={replace}
                    setReplace={setReplace}
                    files={files.filter(file => file.type === "Image")}
                    setFiles={setFiles}
                    instructions={currentInstructions}
                    setInstructions={setCurrentInstructions}
                    handleSave={handleSave}
                />
            ),
            "Additional Details": (
                <AdditionalDetails
                    key={`additional-${designType}`}
                    description={descriptions.additional}
                    handleNavigation={handleNavigation}
                    lab={lab}
                    labID={labID}
                    files={files}
                    instructions={currentInstructions}
                    setInstructions={setCurrentInstructions}
                    setLoading={setLoading}
                    loading={loading}
                    handleSave={handleSave}
                    designType={designType}
                />
            )
        };
        return contentMap[activeTile] || null;
    };

    if (loading.isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress className={classes.loaderClass} />
                <Typography variant="h6" style={{ marginLeft: 16, textTransform: 'none' }}>{loading.message}</Typography>
            </Box>
        );
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Typography variant="h5" style={{}}>
                        Lab Design
                    </Typography>
                    <Typography variant="body1" style={{ marginBottom: "10px" }}>
                        Add the requirements for the lab.
                    </Typography>
                    <Divider my={2}/>
                </Grid>
                <Grid item xs={12} style={{ marginTop: "16px", marginBottom: "16px" }}>
                    <LabDesignTiles
                        color="warning"
                        alignCenter
                        onChange={setActiveTile}
                        active={activeTile}
                        tabs={[
                            { heading: "General Requirements", icon: BuildIcon, description: descriptions.resources, onClick: () => handleNavigation("General Requirements") },
                            { heading: "Data Requirements", icon: StorageIcon, description: descriptions.datasets, onClick: () => handleNavigation("Data Requirements") },
                            { heading: "Visualization Requirements", icon: BarChartIcon, description: descriptions.visualization, onClick: () => handleNavigation("Visualization Requirements") },
                            { heading: "Additional Details", icon: MenuBookIcon, description: descriptions.additional, onClick: () => handleNavigation("Additional Details") }
                        ]}
                    />
                </Grid>
            </Grid>
            {renderContent()}
        </>
    );
}